import React from 'react';
import { Link } from 'react-router-dom';

import './MoatSections.css';

/*
 * Moat Sections - why 8080.AI wins, from the investor briefing.
 *
 * Three quiet blocks between the feature showcase and the trust sections:
 *
 *   the quadratic tax (problem) → flat-cost fix (routed attention,
 *   retrofit, proven) → three multiplying 10× levers
 *
 * Static markup + CSS only (no motion libraries) so the content is fully
 * visible without JavaScript and reads as first-class text to crawlers.
 */

interface StatDef {
  value: string;
  label: string;
}

const TAX_STATS: StatDef[] = [
  { value: '1M+ tokens', label: 'what a real enterprise codebase measures - far beyond what quadratic models can afford to read' },
  { value: '×100 cost', label: 'the quadratic penalty for reading 10× more code - before a single line is written' },
  { value: '2.15× faster', label: 'our flat-cost models on long code - and the gap widens as codebases grow' },
];

interface FixDef {
  num: string;
  title: string;
  body: string;
}

const FIXES: FixDef[] = [
  {
    num: '01',
    title: 'Read what matters, not everything',
    body: 'Our routing layer sends each new word to the few places in memory that matter. Work per word stops growing with document size: O(n²) becomes O(n).',
  },
  {
    num: '02',
    title: 'Retrofit, not rebuild',
    body: 'We convert leading open models in days - no from-scratch pretraining, no new chips. Every new model release makes us better the week it ships.',
  },
  {
    num: '03',
    title: 'Proven on a real model',
    body: 'A 9B open model, converted: flat generation speed at every context we tested - 2.15× faster at 32k, and the gap widens with length.',
  },
];

interface LeverDef {
  factor: string;
  title: string;
  body: string;
}

const LEVERS: LeverDef[] = [
  {
    factor: '10×',
    title: 'Context economics',
    body: 'Routed attention makes reading a whole repository affordable - the cost of context stops growing with the square of its size.',
  },
  {
    factor: '10×',
    title: 'Thinking speed',
    body: 'We train models to run ten independent lines of thought inside a single pass, then merge the best into the answer. Reasoning that took ten passes takes one.',
  },
  {
    factor: '10×',
    title: 'Cost per token',
    body: 'A small, cheap model writes most of the code. We read its confidence word by word; the moment it wavers, a large model steps in, fixes, and hands back.',
  },
];


/* ── Figures - static SVG, all data direct-labeled (no JS required) ──
   Palette validated for the dark surface (#212121): us #4f8ef7, the
   quadratic tax #d97706; identity is never color-alone (direct labels). */

const FIG = { us: '#4f8ef7', tax: '#d97706', grid: 'rgba(255,255,255,0.07)', axis: 'rgba(255,255,255,0.18)', ink: '#86868b', inkStrong: '#d1d1d6' };

const TaxCurveFigure: React.FC = () => (
  <figure className="ms-figure" role="img" aria-label="Cost per word versus context read: standard attention grows with the square of context; routed attention stays flat.">
    <svg viewBox="0 0 560 300" className="ms-figure__svg" aria-hidden="true">
      {/* grid */}
      {[70, 130, 190].map((y) => (
        <line key={y} x1="56" y1={y} x2="532" y2={y} stroke={FIG.grid} strokeWidth="1" />
      ))}
      {/* axes */}
      <line x1="56" y1="250" x2="532" y2="250" stroke={FIG.axis} strokeWidth="1" />
      <line x1="56" y1="34" x2="56" y2="250" stroke={FIG.axis} strokeWidth="1" />
      {/* quadratic tax: y = x² is exactly this quadratic Bézier */}
      <path d="M 56 250 Q 294 250 532 40" fill="none" stroke={FIG.tax} strokeWidth="2" strokeLinecap="round" />
      {/* routed attention: linear, near-flat */}
      <path d="M 56 246 L 532 216" fill="none" stroke={FIG.us} strokeWidth="2" strokeLinecap="round" />
      {/* direct labels */}
      <text x="380" y="92" fill={FIG.tax} fontSize="13" fontWeight="600">standard attention - O(n²)</text>
      <text x="392" y="204" fill={FIG.us} fontSize="13" fontWeight="600">8080 routed - O(n)</text>
      <text x="532" y="272" fill={FIG.ink} fontSize="12" textAnchor="end">context the model reads →</text>
      <text x="50" y="40" fill={FIG.ink} fontSize="12" textAnchor="end" transform="rotate(-90 50 40)" />
      <text x="66" y="46" fill={FIG.ink} fontSize="12">cost per word ↑</text>
    </svg>
    <figcaption className="ms-figure__caption">
      10× the reading → 100× the cost under standard attention. Routing makes
      work per word stop growing with document size.
    </figcaption>
  </figure>
);

const SPEED_X = [2, 4, 8, 16, 32];
const SPEED_STD = [1.0, 0.92, 0.78, 0.6, 0.44];
const SPEED_OURS = [0.98, 0.96, 0.95, 0.95, 0.95];

function speedPath(vals: number[]): string {
  const x0 = 56;
  const w = 476;
  const yOf = (v: number) => 250 - v * 195;
  const xOf = (i: number) => x0 + (w / (vals.length - 1)) * i;

  return vals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i).toFixed(0)} ${yOf(v).toFixed(0)}`).join(' ');
}

const SpeedFigure: React.FC = () => (
  <figure className="ms-figure" role="img" aria-label="Generation speed by context length, same 9B model: standard attention loses 56 percent of throughput from 2k to 32k tokens; the routed retrofit stays flat and is 2.15 times faster at 32k.">
    <svg viewBox="0 0 560 300" className="ms-figure__svg" aria-hidden="true">
      {[70, 130, 190].map((y) => (
        <line key={y} x1="56" y1={y} x2="532" y2={y} stroke={FIG.grid} strokeWidth="1" />
      ))}
      <line x1="56" y1="250" x2="532" y2="250" stroke={FIG.axis} strokeWidth="1" />
      <line x1="56" y1="34" x2="56" y2="250" stroke={FIG.axis} strokeWidth="1" />
      <path d={speedPath(SPEED_STD)} fill="none" stroke={FIG.tax} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d={speedPath(SPEED_OURS)} fill="none" stroke={FIG.us} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* end markers */}
      <circle cx="532" cy={250 - 0.44 * 195} r="4" fill={FIG.tax} stroke="#212121" strokeWidth="2" />
      <circle cx="532" cy={250 - 0.95 * 195} r="4" fill={FIG.us} stroke="#212121" strokeWidth="2" />
      {/* direct labels */}
      <text x="300" y="52" fill={FIG.us} fontSize="13" fontWeight="600">our retrofit - flat, 2.15× faster at 32k</text>
      <text x="330" y="180" fill={FIG.tax} fontSize="13" fontWeight="600">standard attention - −56%</text>
      {/* x ticks */}
      {SPEED_X.map((k, i) => (
        <text key={k} x={56 + (476 / 4) * i} y="270" fill={FIG.ink} fontSize="12" textAnchor="middle">{k}k</text>
      ))}
      <text x="66" y="46" fill={FIG.ink} fontSize="12">generation speed ↑</text>
    </svg>
    <figcaption className="ms-figure__caption">
      Measured: the same 9B open model with standard attention vs our
      retrofit - single GPU, generation speed by context length.
    </figcaption>
  </figure>
);

const QuadrantFigure: React.FC = () => (
  <figure className="ms-figure ms-figure--quadrant" role="img" aria-label="Landscape quadrant: incumbents pay the quadratic tax with GPU capex; new architectures are efficient but must pretrain from scratch; 8080.ai alone combines flat cost with existing models.">
    <svg viewBox="0 0 560 340" className="ms-figure__svg" aria-hidden="true">
      {/* quadrant frame */}
      <line x1="280" y1="30" x2="280" y2="290" stroke={FIG.grid} strokeWidth="1" />
      <line x1="40" y1="160" x2="520" y2="160" stroke={FIG.grid} strokeWidth="1" />
      {/* axis labels */}
      <text x="280" y="318" fill={FIG.ink} fontSize="12" textAnchor="middle">must pretrain from scratch ←   → works on existing models</text>
      <text x="24" y="160" fill={FIG.ink} fontSize="12" textAnchor="middle" transform="rotate(-90 24 160)">quadratic cost ←   → flat cost</text>
      {/* occupants */}
      <g>
        <circle cx="160" cy="95" r="5" fill={FIG.ink} />
        <text x="160" y="72" fill={FIG.inkStrong} fontSize="13" fontWeight="600" textAnchor="middle">New architectures</text>
        <text x="160" y="122" fill={FIG.ink} fontSize="11.5" textAnchor="middle">efficient, but restart every</text>
        <text x="160" y="137" fill={FIG.ink} fontSize="11.5" textAnchor="middle">model generation from scratch</text>
      </g>
      <g>
        <circle cx="400" cy="225" r="5" fill={FIG.ink} />
        <text x="400" y="202" fill={FIG.inkStrong} fontSize="13" fontWeight="600" textAnchor="middle">Incumbents</text>
        <text x="400" y="252" fill={FIG.ink} fontSize="11.5" textAnchor="middle">outrun the tax with GPU capex -</text>
        <text x="400" y="267" fill={FIG.ink} fontSize="11.5" textAnchor="middle">billions a year we simply don&apos;t owe</text>
      </g>
      <g>
        <rect x="290" y="56" width="220" height="68" rx="12" fill="rgba(79,142,247,0.10)" stroke={FIG.us} strokeWidth="1.5" />
        <circle cx="400" cy="78" r="5" fill={FIG.us} />
        <text x="400" y="100" fill="#f5f5f7" fontSize="14" fontWeight="700" textAnchor="middle">8080.ai</text>
        <text x="400" y="115" fill={FIG.us} fontSize="11.5" textAnchor="middle">converts the best open model, in days</text>
      </g>
    </svg>
    <figcaption className="ms-figure__caption">
      The winning quadrant is empty - except for us. The whole ecosystem&apos;s progress compounds into our product.
    </figcaption>
  </figure>
);

const MoatHeader: React.FC<{
  eyebrow: string;
  headingId: string;
  title: React.ReactNode;
  sub?: React.ReactNode;
}> = ({ eyebrow, headingId, title, sub }) => (
  <div className="ms-header">
    <p className="ms-header__eyebrow">{eyebrow}</p>
    <h2 id={headingId} className="ms-header__heading">
      {title}
      {sub && (
        <>
          <br />
          <span className="ms-header__sub">{sub}</span>
        </>
      )}
    </h2>
  </div>
);

const QuadraticTaxSection: React.FC = () => (
  <section id="moat" className="ms-section" aria-labelledby="moat-tax-heading">
    <div className="ms-inner">
      <MoatHeader
        eyebrow="The moat"
        headingId="moat-tax-heading"
        title="AI pays a quadratic tax. We removed it."
        sub="Flat-cost intelligence for software engineering - built on the models the world already has."
      />
      <p className="ms-copy">
        To produce each new word, today&apos;s models re-read everything they have
        read so far: 2× the reading means 4× the cost, 10× the reading means
        100× the cost. Nowhere does that tax bite harder than coding - so
        today&apos;s assistants read fragments, forget the rest, and guess.
      </p>
      <TaxCurveFigure />
      <div className="ms-stats">
        {TAX_STATS.map((s) => (
          <div className="ms-stat" key={s.value}>
            <span className="ms-stat__value">{s.value}</span>
            <span className="ms-stat__label">{s.label}</span>
          </div>
        ))}
      </div>
      <div className="ms-proof">
        <p className="ms-proof__kicker">The breakthrough</p>
        <h3 className="ms-proof__title">8080.AI linear attention</h3>
        <p className="ms-proof__copy">
          Each new word reads a bounded slice of memory, the output is still the exact attention formula,
          and every token can still reach every other token. Flat cost at any context length, on the
          models the world already has, with no retraining. And it ships with something no other attention
          mechanism has: a zero-knowledge proof of its own cost that you can run yourself.
        </p>
        <Link to="/proof" className="ms-proof__btn">
          Run the proof
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </Link>
      </div>
    </div>
  </section>
);

const FlatCostSection: React.FC = () => (
  <section className="ms-section" aria-labelledby="moat-fix-heading">
    <div className="ms-inner">
      <MoatHeader
        eyebrow="The fix"
        headingId="moat-fix-heading"
        title="Flat cost - on models that already exist."
        sub="Routed attention: each new word reads a small, relevant slice of memory instead of everything."
      />
      <div className="ms-cards">
        {FIXES.map((f) => (
          <article className="ms-card" key={f.num}>
            <span className="ms-card__num">{f.num}</span>
            <h3 className="ms-card__title">{f.title}</h3>
            <p className="ms-card__body">{f.body}</p>
          </article>
        ))}
      </div>
      <SpeedFigure />
    </div>
  </section>
);

const LeversSection: React.FC = () => (
  <section className="ms-section" aria-labelledby="moat-levers-heading">
    <div className="ms-inner">
      <MoatHeader
        eyebrow="The multipliers"
        headingId="moat-levers-heading"
        title="Three 10× levers. One platform. They multiply."
        sub="Orders-of-magnitude better unit economics for AI that writes software."
      />
      <div className="ms-levers">
        {LEVERS.map((l, i) => (
          <React.Fragment key={l.title}>
            {i > 0 && <span className="ms-levers__times" aria-hidden="true">×</span>}
            <article className="ms-lever">
              <span className="ms-lever__factor">{l.factor}</span>
              <h3 className="ms-lever__title">{l.title}</h3>
              <p className="ms-lever__body">{l.body}</p>
            </article>
          </React.Fragment>
        ))}
      </div>
      <QuadrantFigure />
      <p className="ms-copy ms-copy--closing">
        Incumbents answer exploding context demand with GPU capex. Our answer
        is math - it ships as software, at software margins, and every new
        open model upgrades us in days. The ecosystem&apos;s R&amp;D compounds into
        our moat.
      </p>
    </div>
  </section>
);

export const MoatSections: React.FC = () => (
  <>
    <QuadraticTaxSection />
    <FlatCostSection />
    <LeversSection />
  </>
);
